import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";

const STORAGE_KEY = "kaforder-color-theme";
const DEFAULT_THEME = "fig-olive";

export const COLOR_THEMES = [
  {
    id: "fig",
    label: "ألوان التين",
    shortLabel: "التين",
    description: "بنفسجي تيني دافئ بهوية موحّدة.",
    swatches: ["#7B284C", "#4A2033", "#F4E8ED"],
    browserColor: "#4A2033",
  },
  {
    id: "olive",
    label: "ألوان الزيتون",
    shortLabel: "الزيتون",
    description: "درجات زيتونية هادئة وطبيعية.",
    swatches: ["#5B6F30", "#35421F", "#EEF1E4"],
    browserColor: "#35421F",
  },
  {
    id: "fig-olive",
    label: "التين والزيتون",
    shortLabel: "التين والزيتون",
    description: "البنفسجي للحركة والزيتوني للثقة.",
    swatches: ["#7B284C", "#404F26", "#FAF9F5"],
    browserColor: "#404F26",
  },
];

const themeIds = new Set(COLOR_THEMES.map((theme) => theme.id));
const ColorThemeContext = createContext(null);

const normalizeTheme = (theme) => themeIds.has(theme) ? theme : DEFAULT_THEME;

function applyTheme(theme) {
  const normalized = normalizeTheme(theme);
  document.documentElement.dataset.colorTheme = normalized;
  document.documentElement.style.colorScheme = "light";
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  const option = COLOR_THEMES.find((item) => item.id === normalized);
  if (metaTheme && option) metaTheme.setAttribute("content", option.browserColor);
}

export function ColorThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      return normalizeTheme(localStorage.getItem(STORAGE_KEY));
    } catch {
      return DEFAULT_THEME;
    }
  });

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (nextTheme) => {
    const normalized = normalizeTheme(nextTheme);
    setThemeState(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      // The theme still applies for this session if storage is unavailable.
    }
  };

  const value = useMemo(() => ({ theme, setTheme, themes: COLOR_THEMES }), [theme]);
  return <ColorThemeContext.Provider value={value}>{children}</ColorThemeContext.Provider>;
}

export function useColorTheme() {
  const context = useContext(ColorThemeContext);
  if (!context) throw new Error("useColorTheme must be used within ColorThemeProvider");
  return context;
}
